import React, { useRef, useState } from 'react';

import { isImagingType, labEntryAt } from '../../constants/lab';
import { formatDateTimeIST, elapsedSince } from '../../utils/formatIST';
import labCardIdentity from './labCardIdentity';
import ConfirmationModal from '../common/ConfirmationModal/ConfirmationModal';

const MAX_REPORT_FILES = 5;

// Lifecycle → display label, status dot, and the technician's next action.
// Wording differs for imaging tests (X-ray/CT/MRI/USG/ECG/Mammography): a
// pathology test has a sample that's "collected", an imaging test is "scanned".
// in_progress completes via a result-file upload (handled below, not onAction).
// Exported (S5) — the collect status-change stripe reuses this SAME "Sample
// collected"/"Scan done" label as its "what just happened" phrase. Start and
// complete stripes use their own fixed verbs ("Sent for processing"/"Sent
// for reporting", "Result published" — business Required change 3), not this
// label — "Processing"/"Completed" describe the CARD'S CURRENT state, not
// what just happened.
export function statusMeta(status, imaging) {
  switch (status) {
    case 'payment_cleared':
      return {
        label: 'Pending', dot: 'bg-amber-500',
        action: { key: 'collect', label: imaging ? '📷 Perform scan' : '🧫 Collect sample' },
      };
    case 'sample_collected':
      return {
        label: imaging ? 'Scan done' : 'Sample collected', dot: 'bg-sky-500',
        // "Start" was ambiguous — this step marks the sample as handed off for
        // analysis/reporting. Imaging reads the scan; pathology runs the sample.
        action: { key: 'start', label: imaging ? '▶ Send for reporting' : '▶ Send for processing' },
      };
    case 'in_progress':
      return { label: imaging ? 'Reporting' : 'Processing', dot: 'bg-blue-500', action: null };
    case 'completed':
      return { label: 'Completed', dot: 'bg-emerald-500', action: null };
    case 'cancelled':
      return { label: 'Cancelled', dot: 'bg-slate-400', action: null };
    case 'standing':
      // A standing-order parent — not a technician work item; shown inert if it
      // ever surfaces here.
      return { label: 'Standing', dot: 'bg-violet-500', action: null };
    default:
      return { label: status, dot: 'bg-slate-400', action: null };
  }
}

// Read a multi-file <input>, cap at MAX_REPORT_FILES, and hand the array
// back. S7 (bk-219) — the over-the-cap notice used to be a bare
// `window.alert(...)`; `onCapped` (optional) lets the caller show it as an
// in-app message instead, same wording, no new microcopy.
function pickFiles(e, cb, onCapped) {
  const all = Array.from(e.target.files || []);
  e.target.value = '';
  if (!all.length) return;
  if (all.length > MAX_REPORT_FILES && onCapped) onCapped();
  cb(all.slice(0, MAX_REPORT_FILES));
}

// Not-started states share the single cancel/refund boundary with the backend:
// once a sample is collected the test can't be cancelled (Reject sample instead).
const NOT_STARTED_STATES = ['advised', 'awaiting_payment', 'payment_cleared', 'scheduled'];

export default function LabOrderCard({
  order, busy, hidePatient, showToken, nowMs, onAction, onComplete, onAttach, onViewReport, onPrintReport, onReplace, onDelete,
  onReject, onCancel, onPrintLabel,
  // S5 (status-change feedback, "stay then move") — `stripe` is
  // { tone: 'ok'|'warn', content: <node>, onUndo: (fn|null) }, rendered while
  // a deferred action is held for this order; `glow`/`glowTag` mark the card
  // for a couple of seconds right after it lands in its new lane.
  stripe, glow, glowTag,
}) {
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const attachRef = useRef(null);
  // S7 (bk-219) — the "too many files" notice, in-app instead of window.alert.
  const [fileCapNotice, setFileCapNotice] = useState(false);
  const imaging = isImagingType(order.specimen_type);
  const meta = statusMeta(order.status, imaging);
  const completedNoReport = order.status === 'completed' && !(order.report_count > 0);

  // Identity block (S3, mockup 02 — business-ratified) — the name is on
  // EVERY card, bold, line 1, even inside a grouped patient block where the
  // block header repeats it (wrong-patient prevention is the point; the
  // repeat is deliberate, not a bug). `hidePatient` only still gates the
  // card's own `#L-n` token — the block header carries one shared token when
  // every order in the block shares it (D2), so the card re-shows its own
  // only when the header couldn't. Mobile/UHID/receipt/ward-bed are always
  // shown when present, each line simply omitted when the underlying field
  // is absent (never a blank line, never "null").
  const identity = labCardIdentity(order);
  const showIdentityName = !!identity.name;
  const showIdentityToken = (!hidePatient || showToken) && identity.labToken != null;
  const identityChips = [];
  if (identity.mobile) {
    identityChips.push({ key: 'mobile', node: <span>📞 {identity.mobile}</span> });
  }
  if (identity.uhid) {
    identityChips.push({
      key: 'uhid',
      node: (
        <span className="rounded bg-slate-100 dark:bg-slate-700 px-1 py-0.5 font-mono">{identity.uhid}</span>
      ),
    });
  }
  if (identity.receipt) {
    identityChips.push({
      key: 'receipt',
      node: (
        <span className="rounded bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 px-1.5 py-0.5 font-bold">
          💰 Receipt #{identity.receipt}
        </span>
      ),
    });
  }
  // Ward/bed only — the IP number itself already sits on the block header
  // (D2's IP badge); repeating it bare here would duplicate that exact text.
  if (identity.wardBed) {
    identityChips.push({
      key: 'wardbed',
      node: (
        <span className="rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-700 px-1.5 py-0.5 font-bold">
          🛏 {identity.wardBed}
        </span>
      ),
    });
  }
  const hasIdentityBlock = showIdentityName || showIdentityToken || identityChips.length > 0;
  // A sample number exists once collected; it's the identity that ties this
  // physical tube/sample to its report. Shown verbatim (typed or auto-minted).
  const inProcess = order.status === 'sample_collected' || order.status === 'in_progress';

  // Lifecycle dates (IST) — only the milestones the order has actually reached,
  // so a re-collected order after a rejection shows no stale started_at.
  const reachedCollected = ['sample_collected', 'in_progress', 'completed'].includes(order.status);
  const reachedProcessing = ['in_progress', 'completed'].includes(order.status);
  // "Arrived" = when the order entered the lab queue (lab arrival), not the
  // advise time — falls back to created_at for legacy rows.
  const arrivedAt = labEntryAt(order);
  const steps = [];
  if (arrivedAt) steps.push(['Arrived', arrivedAt]);
  if (reachedCollected && order.specimen_collected_at) steps.push([imaging ? 'Scanned' : 'Collected', order.specimen_collected_at]);
  if (reachedProcessing && order.started_at) steps.push([imaging ? 'Reporting' : 'Processing', order.started_at]);
  if (order.status === 'completed' && order.completed_at) steps.push(['Completed', order.completed_at]);
  // While active, lead with how long it's been waiting at its CURRENT step
  // (seconds-precise) so the longest-waiting sample is obvious.
  let waiting = null;
  if (order.status === 'payment_cleared' && arrivedAt) {
    waiting = `Waiting ${elapsedSince(arrivedAt, nowMs)}`;
  } else if (order.status === 'sample_collected' && order.specimen_collected_at) {
    waiting = `Waiting ${elapsedSince(order.specimen_collected_at, nowMs)}`;
  } else if (order.status === 'in_progress' && order.started_at) {
    waiting = `${imaging ? 'Reporting' : 'Processing'} ${elapsedSince(order.started_at, nowMs)}`;
  }

  const outlineBtn =
    'rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-3 py-1.5 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60';

  return (
    <div
      className={`relative rounded-xl border bg-white dark:bg-slate-800 p-3 shadow-sm transition-shadow ${
        glow
          ? 'border-teal-500 dark:border-teal-400 ring-2 ring-teal-300 dark:ring-teal-700 shadow-lg'
          : 'border-slate-300 dark:border-slate-600'
      }`}
    >
      {glow && glowTag && (
        <span className="absolute -top-2 right-3 rounded-full bg-teal-600 text-white text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 shadow">
          {glowTag}
        </span>
      )}
      {/* S5 status-change stripe — the card stays put wearing this instead of
          jumping lanes immediately; Undo (when offered) cancels the still-
          unsent deferred action. */}
      {stripe && (
        <div
          className={`-mx-3 -mt-3 mb-2 rounded-t-xl px-3 py-2 text-xs font-extrabold flex items-center gap-2 flex-wrap ${
            stripe.tone === 'warn'
              ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200 border-b border-rose-300 dark:border-rose-700'
              : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 border-b border-emerald-300 dark:border-emerald-700'
          }`}
        >
          <span>{stripe.content}</span>
          {stripe.onUndo && (
            <button onClick={stripe.onUndo} className="ml-auto underline font-extrabold">
              Undo
            </button>
          )}
        </div>
      )}
      {/* Identity block (S3, mockup 02) — a fixed two-line header: line 1 is
          the name (when not already shown by the patient block above) plus
          the card's own `#L-n` (+ OPD token); line 2 is mobile · UHID ·
          receipt · ward/bed, each segment simply absent when unknown. */}
      {hasIdentityBlock && (
        <div className="pb-2 mb-2 border-b border-dashed border-slate-200 dark:border-slate-700">
          {(showIdentityName || showIdentityToken) && (
            <div className="flex items-baseline gap-1.5 flex-wrap">
              {showIdentityName && (
                <span className="font-extrabold text-sm text-slate-800 dark:text-slate-100">{identity.name}</span>
              )}
              {showIdentityToken && (
                <span className="text-slate-500 dark:text-slate-400 font-bold text-xs">
                  #L-{identity.labToken}{identity.opdToken != null ? ` (Q-${identity.opdToken})` : ''}
                </span>
              )}
            </div>
          )}
          {identityChips.length > 0 && (
            <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
              {identityChips.map((chip, i) => (
                <React.Fragment key={chip.key}>
                  {i > 0 && <span className="text-slate-300 dark:text-slate-600">·</span>}
                  {chip.node}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-bold text-slate-800 dark:text-slate-100 truncate">{order.test_name}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex flex-wrap gap-2 items-center">
            {order.specimen_type && (
              <span className="rounded bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5">{order.specimen_type}</span>
            )}
            {order.sample_number && (
              <span className="rounded bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-700 px-1.5 py-0.5 font-semibold tracking-wide">
                🏷 {order.sample_number}
              </span>
            )}
            {order.is_external && (
              <span className="rounded bg-violet-50 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 border border-violet-300 dark:border-violet-700 px-1.5 py-0.5 font-semibold">
                External
              </span>
            )}
            {order.status === 'completed' && !order.is_external && order.tat_minutes != null && (
              <span className="rounded bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 border border-teal-300 dark:border-teal-700 px-1.5 py-0.5 font-semibold">
                TAT {order.tat_minutes}m
              </span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
            <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
            {meta.label}
          </span>
          {order.final_cost != null && (
            <div className="text-sm font-extrabold text-slate-800 dark:text-slate-100 mt-1">
              ₹{Number(order.final_cost).toFixed(0)}
            </div>
          )}
        </div>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        {meta.action && (
          <button
            onClick={() => onAction(meta.action.key, order)}
            // Disabled while THIS card's own hold is pending (S5) — a second
            // tap on the same forward action is a no-op, not a second hold;
            // Reject/Cancel stay reachable so they can flush it if needed.
            disabled={busy || !!stripe}
            className="rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
          >
            {busy ? 'Working…' : meta.action.label}
          </button>
        )}

        {/* Cancel a not-started order (sample not yet collected). Refundable —
            the receptionist issues the refund. Once collected, Reject sample is
            shown instead (the same boundary the backend enforces). */}
        {onCancel && NOT_STARTED_STATES.includes(order.status) && (
          <button
            onClick={() => onCancel(order)}
            disabled={busy}
            className="rounded-lg border border-rose-300 dark:border-rose-700 bg-white dark:bg-slate-800 text-rose-600 dark:text-rose-300 px-3 py-1.5 text-sm font-semibold hover:bg-rose-50 dark:hover:bg-rose-900/30 disabled:opacity-60"
          >
            ✕ Cancel order
          </button>
        )}

        {order.status === 'in_progress' && onComplete && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/*"
              multiple
              className="hidden"
              onChange={(e) => pickFiles(e, (files) => onComplete(order, files), () => setFileCapNotice(true))}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
            >
              {busy ? 'Uploading…' : `✓ Complete with result${imaging ? '/images' : ''}`}
            </button>
          </>
        )}

        {/* In-process (collected / processing) — reprint the sample label or
            reject a bad sample for re-collection. */}
        {inProcess && onPrintLabel && order.sample_number && (
          <button onClick={() => onPrintLabel(order)} className={outlineBtn}>
            🏷 Print label
          </button>
        )}
        {inProcess && onReject && (
          <button
            onClick={() => onReject(order)}
            disabled={busy}
            className="rounded-lg border border-rose-300 dark:border-rose-700 bg-white dark:bg-slate-800 text-rose-600 dark:text-rose-300 px-3 py-1.5 text-sm font-semibold hover:bg-rose-50 dark:hover:bg-rose-900/30 disabled:opacity-60"
          >
            ↩ Reject sample
          </button>
        )}

        {/* Completed with at least one live report — view / print / replace /
            delete. Print (S9) reuses the SAME view/open path, then fires the
            print dialog — no new endpoint, no separate print flow. */}
        {order.status === 'completed' && order.report_count > 0 && (
          <>
            {onViewReport && (
              <button onClick={() => onViewReport(order)} className={outlineBtn}>
                📄 View report
              </button>
            )}
            {onPrintReport && (
              <button onClick={() => onPrintReport(order)} className={outlineBtn}>
                🖨 Print report
              </button>
            )}
            {onReplace && (
              <>
                <input
                  ref={replaceRef}
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onReplace(order, f);
                    e.target.value = '';
                  }}
                />
                <button onClick={() => replaceRef.current?.click()} disabled={busy} className={outlineBtn}>
                  ↻ Replace
                </button>
              </>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(order)}
                disabled={busy}
                className="rounded-lg border border-rose-300 dark:border-rose-700 bg-white dark:bg-slate-800 text-rose-600 dark:text-rose-300 px-3 py-1.5 text-sm font-semibold hover:bg-rose-50 dark:hover:bg-rose-900/30 disabled:opacity-60"
              >
                🗑 Delete
              </button>
            )}
          </>
        )}

        {/* Completed but no live report (e.g. the only one was deleted) —
            let the technician attach a fresh report without re-opening the test. */}
        {completedNoReport && onAttach && (
          <>
            <input
              ref={attachRef}
              type="file"
              accept="application/pdf,image/*"
              multiple
              className="hidden"
              onChange={(e) => pickFiles(e, (files) => onAttach(order, files), () => setFileCapNotice(true))}
            />
            <button onClick={() => attachRef.current?.click()} disabled={busy} className={outlineBtn}>
              {busy ? 'Uploading…' : '📎 Attach report'}
            </button>
          </>
        )}
      </div>

      {/* Lifecycle dates (IST, with seconds). Active cards lead with how long
          the test has been waiting at its current step; completed cards show
          the full Arrived → Collected → Processing → Completed trail. */}
      {steps.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700 text-[11px] text-slate-500 dark:text-slate-400 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {waiting && (
            <span className="font-bold text-slate-700 dark:text-slate-200">🕐 {waiting}</span>
          )}
          {steps.map(([label, ts]) => (
            <span key={label} className="whitespace-nowrap">
              <span className="text-slate-400 dark:text-slate-500">{label}</span> {formatDateTimeIST(ts)}
            </span>
          ))}
        </div>
      )}

      {/* S7 (bk-219) — the over-5-files notice, in-app instead of
          window.alert. Same wording as the old alert, just not a native
          popup; single OK button (no reason, nothing to confirm). */}
      <ConfirmationModal
        isOpen={fileCapNotice}
        icon="📎"
        title="Too many files"
        message={`You can attach up to ${MAX_REPORT_FILES} files at a time — using the first ${MAX_REPORT_FILES}.`}
        ctaLabel="OK"
        onConfirm={() => setFileCapNotice(false)}
      />
    </div>
  );
}
